/**
 * Server-side Hebrew formatting — used ONLY for the strings the API delivers as ready text
 * (docs/api-business.md §1.3): alert titles/bodies, forecast line labels, tax-event `basisLabel`,
 * VAT period labels, reminder drafts and CSV headers. Every other number crosses the wire as integer
 * agorot and is formatted by `web/src/format.ts`.
 */
import type { DocType, ExpenseCategory, PaymentChannel, ServiceLine, TxScope } from '../../api-types.ts';

const NUM = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 });
const NUM2 = new Intl.NumberFormat('he-IL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** `₪1,234` / `−₪1,234` (U+2212 minus before the sign, spec §8). Rounded to whole shekels. */
export function fmtMoney(agorot: number | null | undefined, decimals = 0): string {
  if (agorot === null || agorot === undefined) return '—';
  const neg = agorot < 0;
  const abs = Math.abs(agorot);
  const text = decimals === 2 ? NUM2.format(abs / 100) : NUM.format(Math.round(abs / 100));
  return `${neg ? '−' : ''}₪${text}`;
}

/** `dd.mm.yyyy` */
export function fmtDay(day: string): string {
  return `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;
}

/** `dd.mm` — cards and chips (spec §4 date convention). */
export function fmtDayShort(day: string): string {
  return `${Number(day.slice(8, 10))}.${Number(day.slice(5, 7))}`;
}

const MONTH_NAMES = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני', 'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];
/** Short Hebrew month names for the bi-monthly VAT period chips ("יולי–אוג׳ 26"). */
const MONTH_SHORT = ['ינו׳', 'פבר׳', 'מרץ', 'אפר׳', 'מאי', 'יוני', 'יולי', 'אוג׳', 'ספט׳', 'אוק׳', 'נוב׳', 'דצמ׳'];

export function monthName(month: string): string {
  return MONTH_NAMES[Number(month.slice(5, 7)) - 1] ?? month;
}

export function monthShort(month: string): string {
  return MONTH_SHORT[Number(month.slice(5, 7)) - 1] ?? month;
}

/** "יוני–אוגוסט" for a baseline window. */
export function monthsLabel(months: readonly string[]): string {
  if (months.length === 0) return '';
  const first = months[0];
  const last = months[months.length - 1];
  if (!first || !last) return '';
  return first === last ? monthName(first) : `${monthName(first)}–${monthName(last)}`;
}

/** `****7712` — the mask used in every alert title and card label. */
export function maskLast4(last4: string | null): string {
  return last4 ? `****${last4}` : '';
}

/** `+40%` / `−12%` for the anomaly chips. */
export function fmtPctDelta(ratio: number | null): string {
  if (ratio === null) return '';
  const pct = Math.round((ratio - 1) * 100);
  return `${pct >= 0 ? '+' : '−'}${Math.abs(pct)}%`;
}

/** "נותרו 9 ימים" / "היום" / "באיחור 3 ימים". */
export function fmtDaysUntil(days: number): string {
  if (days === 0) return 'היום';
  if (days === 1) return 'מחר';
  if (days > 0) return `נותרו ${days} ימים`;
  return `באיחור ${Math.abs(days)} ימים`;
}

export const EXPENSE_CATEGORY_LABELS: Readonly<Record<ExpenseCategory, string>> = {
  EQUIPMENT: 'ציוד וחלפים',
  SOFTWARE: 'תוכנה ושירותים',
  TELECOM: 'תקשורת',
  VEHICLE: 'רכב ודלק',
  MARKETING: 'שיווק',
  BOOKKEEPING: 'הנה"ח ועמלות',
  CONTRACTORS: 'שכר/קבלנים',
};

export const SCOPE_LABELS: Readonly<Record<TxScope, string>> = {
  expense: 'הוצאה',
  receipt: 'תקבול',
  tax: 'מסים',
  card_settlement: 'חיוב כרטיס',
  owner_draw: 'משיכת בעלים',
  transfer: 'העברה פנימית',
  ignore: 'לא נספר',
};

export const DOC_TYPE_LABELS: Readonly<Record<DocType, string>> = {
  proforma: 'חשבונית עסקה',
  tax: 'חשבונית מס',
  tax_receipt: 'חשבונית מס-קבלה',
  receipt: 'קבלה',
  credit: 'חשבונית זיכוי',
};

export const PAYMENT_CHANNEL_LABELS: Readonly<Record<PaymentChannel, string>> = {
  card: 'כרטיס',
  bank_transfer: 'העברה',
  standing_order: 'הוראת קבע',
};

export const SERVICE_LINE_LABELS: Readonly<Record<ServiceLine, string>> = {
  subscriptions: 'מנויים',
  field: 'טכנאי בשטח',
  remote: 'תמיכה מרחוק',
};

export const PROVIDER_LABELS: Readonly<Record<string, string>> = { discount: 'דיסקונט', max: 'Max', cal: 'Cal' };

export function providerLabel(id: string): string {
  return PROVIDER_LABELS[id] ?? id;
}

/** "1.10, 3.10" — the `whenLabel` of a forecast line that lands on several days. */
export function fmtDayList(days: readonly string[]): string {
  return [...new Set(days)].sort().map(fmtDayShort).join(', ');
}
